import React, { ReactNode, useCallback, useState } from 'react'
import { api } from '../common/api'
import { useFetchData } from '../common/servercall/useFetchData'
import { WithErrorAndFetching } from '../common/servercall/WithErrorAndFetching'
import { Box, IconButton, Stack, Typography } from '@mui/material'
import { ModeEdit } from '@mui/icons-material'
import { usePrivilege } from '../common/util/hooks'
import { FeaturePrivileges } from '../common/util/featurePrivileges'
import { AliasForm } from './AliasForm'

export const Alias = ({productId}: { productId: string | undefined }) => {
    const [editingMode, setEditingMode] = useState<boolean>(false)
    const { onlyWithAccess: onlyWithWriteAccess } = usePrivilege(FeaturePrivileges.site.write)

    const fetchSiteAlias = useCallback(
        async (): Promise<string> => {
            if (!productId) return ''
            const {data} = await api.get(`/api/site/${productId}/alias`)
            return data || ' '
        },
        [productId],
    )

    const {
        data: siteAlias,
        isFetching: fetchingAlias,
        error: aliasError,
        refetch,
    } = useFetchData<string>(useCallback(() => fetchSiteAlias(), [fetchSiteAlias]))

    const nonEditing = useCallback((siteAlias: string): ReactNode => {
            return (
                <Stack direction={'row'}>
                    <Box alignSelf={'center'} sx={{paddingRight: 1}}>
                        <Typography variant={'body1'} component={'span'}>
                            {siteAlias}
                        </Typography>
                    </Box>
                    {onlyWithWriteAccess(<Box alignSelf={'center'}>
                        <IconButton size={'small'} color='secondary' aria-label='Change main number'
                                    component='span'
                                    onClick={() => {
                                        setEditingMode(true)
                                    }}>
                            <ModeEdit/>
                        </IconButton>
                    </Box>)}
                </Stack>
            )
        },
        [setEditingMode, onlyWithWriteAccess],
    )

    const renderAlias = useCallback(
        (alias: string): ReactNode => {
            return (<>{editingMode ? <AliasForm onSuccess={() => {
                        refetch()
                        setEditingMode(false)
                    }} onCancel={() => {
                        setEditingMode(false)
                    }} productId={productId} aliasForm={{alias: alias === ' ' ? '' : alias}}/>
                    : nonEditing(alias)}
                </>
            )
        },
        [editingMode, setEditingMode, nonEditing, refetch, productId],
    )

    return <WithErrorAndFetching<string> isFetching={fetchingAlias} error={aliasError} data={siteAlias}
                                         render={renderAlias}/>
}